import React, {useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {zodResolver} from '@hookform/resolvers/zod';
import {useFocusEffect} from '@react-navigation/native';
import {observer} from 'mobx-react-lite';

import BackButton from '../../../components/rewards/BackButton';
import RewardsProgramImage from '../../../components/rewards/ProgramImage';
import Button from '../../../components/themed/Button';
import Text from '../../../components/themed/Text';
import Otp from '../../../components/ui-controls/form/Otp';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import {AppRoute} from '../../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import * as RootNavigation from '../../../navigation/RootNavigation';
import {useStores} from '../../../stores/use-stores';
import apiService from '../../../transport/apiService';
import {OtpVerificationFormData, otpVerificationSchema} from '../../account/schemas/authSchemas';
import {styles} from '../styles';

/**
 * Timeout for sending the code
 */
const TIMEOUT = 30000;

/**
 * Disable the resend link until this much time
 */
const RESEND_TIMEOUT = 10000;

/**
 * Wait for [delay] ms if [promise] returns first.
 * @param promise
 * @param delay
 * @returns
 */
async function slowDown<T>(promise: Promise<T>, delay: number = RESEND_TIMEOUT) {
  const [value] = await Promise.all([
    promise,
    new Promise(resolve => {
      setTimeout(resolve, delay);
    }),
  ]);

  return value as T;
}

const RewardsActivateVerificationScreen = observer(
  ({
    route: _route,
    navigation,
  }: RouteNavigatorScreenProps<AppRoute.REWARDS_ACTIVATE_VERIFICATION>) => {
    const {userStore, rewardsStore, cartStore, shopSelectionStore} = useStores();

    const [resending, setResending] = useState(false);
    const [codeResent, setCodeResent] = useState(false);

    const phoneNumber = userStore.phoneNumber || '';

    const {
      control,
      handleSubmit,
      formState: {errors, touchedFields, isSubmitting},
      setError,
      reset,
    } = useForm<OtpVerificationFormData>({
      resolver: zodResolver(otpVerificationSchema),
      defaultValues: {verification: ''},
      mode: 'onTouched',
    });

    // DIS-474: Reset form state when screen loses focus to prevent stale OTP codes
    // persisting between visits and breaking automation tests
    useFocusEffect(
      React.useCallback(() => {
        // Cleanup function runs when screen blurs
        return () => {
          setResending(false);
          setCodeResent(false);
          reset({verification: ''});
        };
      }, [reset]),
    );

    const onSubmit = async (values: OtpVerificationFormData) => {
      const selectedStore = shopSelectionStore.selectedStoreSummary;

      if (!selectedStore) {
        setError('verification', {type: 'manual', message: 'No selected store'});
        return;
      }

      const optInResponse = await apiService.rewardsOptIn({
        phoneNumber,
        verificationCode: values.verification,
        dateOfBirth: userStore.dateOfBirth,
        emailAddress: userStore.emailAddress,
        firstName: userStore.firstName,
        lastName: userStore.lastName,
        selectedStore: selectedStore,
      });

      if (optInResponse) {
        await rewardsStore.loadRewardsForUser(phoneNumber);

        // reload the cart to apply rewards pricing to existing cart items
        cartStore.fullyReloadOrderFromMFL();

        navigation.navigate(AppRoute.REWARDS_ACTIVATE_SUCCESS, {
          flow: 'activate',
        }); // Success page
      } else {
        setError('verification', {
          type: 'manual',
          message: 'Wrong code entered. Please try again.',
        });
      }
    };

    const resendCode = () => async () => {
      reset({verification: ''});
      setResending(true);

      // apiService can stall so set timeout here
      const resendTimeout = setTimeout(() => {
        setResending(false);
        setCodeResent(false);
      }, TIMEOUT);

      try {
        // resend code can use rewardsVerifyExistingUserPhoneNumber
        // for existing and new user. there are no side effects
        const result = await slowDown(
          apiService.rewardsVerifyExistingUserPhoneNumber({
            phoneNumber: userStore.phoneNumber,
          }),
        );

        const success = result?.status === 200;
        setCodeResent(success);
      } catch (_e) {
        setCodeResent(false);
      }

      clearTimeout(resendTimeout);
      setResending(false);
    };

    return (
      <KeyboardAwareScrollView
        style={{backgroundColor: 'white', flex: 1}}
        contentContainerStyle={{flex: 1}}
        keyboardShouldPersistTaps="always"
        enableOnAndroid
        extraScrollHeight={40}>
        <ContentView parentStyle={{flex: 1, paddingBottom: 16}} style={{flex: 1}}>
          <BackButton
            onPress={() => {
              if (navigation.canGoBack()) {
                navigation.goBack();
              }
            }}
          />
          <RewardsProgramImage variant="password" />
          <Text variant="heading1" style={{marginTop: 20}}>
            {codeResent ? 'CODE RE-SENT' : "WE'VE SENT THE CODE"}
          </Text>

          {codeResent ? (
            <Text style={{marginBottom: 20}}>We have re-sent your code to your mobile.</Text>
          ) : null}

          <View style={{flexDirection: 'row', flexWrap: 'wrap'}}>
            <Text>Please verify your number by entering the code sent to {phoneNumber}. </Text>
            <Text
              onPress={() => {
                // Navigate from ROOT_REWARDS_FLOW to ROOT > MAIN > ACCOUNT_DETAIL
                RootNavigation.navigate(AppRoute.ROOT, {
                  screen: AppRoute.MAIN,
                  params: {
                    screen: AppRoute.ACCOUNT_DETAIL,
                  },
                });
              }}
              style={{color: '#0054A6'}}>
              Change number
            </Text>
          </View>

          <Text
            variant="body2"
            style={[
              {marginTop: 20, marginBottom: 8},
              !!errors.verification && touchedFields.verification && {color: '#C81E1E'},
            ]}>
            Enter code:
          </Text>

          <Controller
            control={control}
            name="verification"
            render={({field: {onChange, onBlur, value}}) => (
              <Otp
                value={value}
                numInputs={6}
                onChangeText={onChange}
                onEnter={handleSubmit(onSubmit)}
                onBlur={onBlur}
                error={!!errors.verification && !!touchedFields.verification}
              />
            )}
          />

          <Text style={styles.errorMessage}>
            {touchedFields.verification && errors.verification ? errors.verification.message : ' '}
          </Text>

          <View
            style={{
              backgroundColor: '#F9FAFB',
              borderRadius: 8,
              height: 52,
              justifyContent: 'center',
              paddingHorizontal: 12,
            }}>
            <Text variant="body2" style={{color: '#6B7280'}}>
              {"Didn't get a code? "}
              {resending ? (
                <Text variant="body2">Click to resend</Text>
              ) : (
                <Text onPress={resendCode()} variant="body2" style={{color: '#1A56DB'}}>
                  Click to resend
                </Text>
              )}
            </Text>
          </View>

          <View style={{marginTop: 32}}>
            <Button
              title={'VERIFY'}
              onPress={handleSubmit(onSubmit)}
              disabled={isSubmitting}
              loading={isSubmitting}
            />
          </View>
        </ContentView>
      </KeyboardAwareScrollView>
    );
  },
);

export default RewardsActivateVerificationScreen;
